import { supabase } from "@/integrations/supabase/client";

export type AppRole = "super_admin" | "admin" | "supplier" | "supplier_owner" | "couple" | "venue_owner";

export const ROLE_RANK: AppRole[] = ["super_admin", "admin", "venue_owner", "supplier_owner", "supplier", "couple"];

/** Highest-privilege role, independent of row order. Unknown values are ignored. */
export const highestRole = (roles: (string | null | undefined)[]): AppRole | null => {
  for (const r of ROLE_RANK) if (roles.includes(r)) return r;
  return null;
};

export const getPrimaryRole = (roles: AppRole[]): AppRole | null => highestRole(roles);

/** Groups user_roles rows by user so a list page needs one query, not one per user. */
export const rolesByUser = (rows: { user_id: string; role: string }[]) => {
  const m = new Map<string, AppRole[]>();
  for (const r of rows) {
    const list = m.get(r.user_id) || [];
    list.push(r.role as AppRole);
    m.set(r.user_id, list);
  }
  return m;
};

export const getUserRoles = async (userId: string): Promise<AppRole[]> => {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);

  if (error) {
    console.error("Unable to load user roles", error);
    return [];
  }

  return (data ?? [])
    .map((row) => (row as { role?: AppRole | null }).role)
    .filter((role): role is AppRole => Boolean(role));
};

export const resolveOwnedBusinessRole = async (userId: string): Promise<AppRole | null> => {
  const { data, error } = await supabase
    .from("suppliers")
    .select("supplier_type")
    .eq("owner_user_id", userId)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("Unable to check owned supplier profile", error);
    return null;
  }

  if (!data) return null;

  return data.supplier_type === "venue" ? "venue_owner" : "supplier";
};

export const resolveUserRoleState = async (userId: string) => {
  const userRoles = await getUserRoles(userId);
  const resolvedRoles = [...new Set(userRoles)];
  let userRole = getPrimaryRole(resolvedRoles);

  if (!userRole || userRole === "couple") {
    const ownedBusinessRole = await resolveOwnedBusinessRole(userId);
    if (ownedBusinessRole) {
      userRole = ownedBusinessRole;
      if (!resolvedRoles.includes(ownedBusinessRole)) resolvedRoles.push(ownedBusinessRole);
    }
  }

  return {
    userRoles: resolvedRoles,
    userRole,
    isAdmin: resolvedRoles.includes("super_admin") || resolvedRoles.includes("admin"),
  };
};

export const resolveBusinessAccessRole = async (userId: string): Promise<AppRole | null> => {
  const { userRoles, userRole } = await resolveUserRoleState(userId);

  if (userRoles.includes("super_admin")) return "super_admin";
  if (userRoles.includes("admin")) return "admin";
  if (userRoles.includes("venue_owner")) return "venue_owner";
  if (userRoles.includes("supplier") || userRole === "supplier_owner") return "supplier";

  return null;
};